import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, AlertTriangle, Clock, Plus, Save, Wrench, Circle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH = {
  green: { clr: "text-emerald-400", bg: "bg-emerald-400", label: "Good" },
  yellow: { clr: "text-amber-400", bg: "bg-amber-400", label: "Due Soon" },
  red: { clr: "text-red-400", bg: "bg-red-400", label: "Attention Needed" },
};
const SEV = {
  overdue: "bg-red-500/15 text-red-300 border-red-500/30",
  soon: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  ready: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};
const field = "w-full px-2.5 py-1.5 rounded-md bg-black border border-white/15 text-white text-sm focus:border-[#F2B705] outline-none";
const lbl = "text-white/50 text-[11px] font-semibold mb-1 block";

const TRACK_FIELDS = [
  ["vin", "VIN", "text"], ["registration_expiry", "Registration Expiry", "date"],
  ["inspection_due", "Inspection Due", "date"], ["insurance_expiry", "Insurance Expiry", "date"],
  ["plate_renewal_date", "Plate Renewal", "date"], ["warranty_expiry_date", "Warranty Expiry (date)", "date"],
  ["warranty_expiry_mileage", "Warranty Expiry (mi)", "number"], ["tire_install_date", "Tire Install Date", "date"],
  ["tire_install_mileage", "Tire Install Mileage", "number"], ["tire_life_miles", "Tire Life (mi)", "number"],
  ["tire_tread_depth", "Tread Depth", "text"], ["battery_install_date", "Battery Install Date", "date"],
  ["battery_install_mileage", "Battery Install Mileage", "number"], ["odometer", "Current Odometer", "number"],
];

export default function VehicleMaintProfile({ token, vehicleId, onBack, onNewWorkOrder, onEditWorkOrder, refreshKey }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [track, setTrack] = useState({});
  const [savingTrack, setSavingTrack] = useState(false);
  const [showTrack, setShowTrack] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/vehicles/${vehicleId}/maintenance-profile`, auth);
      setData(data);
      setTrack({ ...data.vehicle });
    } catch {
      toast.error("Failed to load vehicle profile");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vehicleId, refreshKey]);

  const saveTrack = async () => {
    setSavingTrack(true);
    const payload = {};
    TRACK_FIELDS.forEach(([k, , type]) => { payload[k] = type === "number" ? Number(track[k]) || 0 : (track[k] || ""); });
    try {
      await axios.patch(`${API}/vehicles/${vehicleId}/tracking`, payload, auth);
      toast.success("Tracking info saved");
      setShowTrack(false);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingTrack(false);
    }
  };

  if (loading && !data) return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="animate-spin" size={18} /> Loading profile…</div>;
  if (!data) return null;
  const h = HEALTH[data.health] || HEALTH.green;

  return (
    <div className="space-y-5" data-testid="vehicle-maint-profile">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={onBack} className="flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold"><ArrowLeft size={16} /> Back to Fleet</button>
        <button data-testid="profile-new-wo-btn" onClick={() => onNewWorkOrder(vehicleId)} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold"><Plus size={15} /> New Work Order</button>
      </div>

      <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${h.bg}`} />
          <div>
            <h2 className="text-xl font-bold text-white">{data.vehicle.name}</h2>
            <p className={`text-sm font-semibold ${h.clr}`}>{h.label} · {data.vehicle.odometer.toLocaleString()} mi{data.vehicle.license_plate ? ` · ${data.vehicle.license_plate}` : ""}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Stat label="Total Maint. Cost" value={`$${data.stats.total_maintenance_cost.toLocaleString()}`} />
          <Stat label="Cost / Mile" value={`$${data.stats.cost_per_mile}`} />
          <Stat label="Service Records" value={data.stats.records} />
          <Stat label="Open Work Orders" value={data.stats.open_count} />
        </div>
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4" data-testid="profile-alerts">
          <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><AlertTriangle size={15} className="text-amber-400" /> Alerts</h3>
          <div className="space-y-2">
            {data.alerts.map((a, i) => (
              <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${SEV[a.severity]}`}>
                <div><div className="text-sm font-semibold">{a.title}</div><div className="text-xs opacity-80">{a.detail}</div></div>
                <span className="text-[11px] uppercase font-bold">{a.severity}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tracking info editor */}
      <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4">
        <button onClick={() => setShowTrack((s) => !s)} data-testid="profile-tracking-toggle" className="w-full flex items-center justify-between text-white font-semibold">
          <span>Vehicle Tracking Info <span className="text-white/40 text-xs font-normal">(warranty, registration, tires, battery…)</span></span>
          <span className="text-[#F2B705] text-sm">{showTrack ? "Hide" : "Edit"}</span>
        </button>
        {showTrack && (
          <div className="mt-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {TRACK_FIELDS.map(([k, label, type]) => (
                <div key={k}>
                  <label className={lbl}>{label}</label>
                  <input type={type} className={field} value={track[k] ?? ""} onChange={(e) => setTrack((s) => ({ ...s, [k]: e.target.value }))} data-testid={`track-${k}`} />
                </div>
              ))}
            </div>
            <button data-testid="profile-save-tracking" onClick={saveTrack} disabled={savingTrack} className="mt-3 flex items-center gap-2 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-semibold disabled:opacity-60">
              {savingTrack ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Tracking Info
            </button>
          </div>
        )}
      </div>

      {/* Open work orders */}
      {data.open_work_orders.length > 0 && (
        <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4">
          <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Wrench size={15} className="text-[#F2B705]" /> Open Work Orders</h3>
          <div className="space-y-2">
            {data.open_work_orders.map((w) => (
              <div key={w.id} onClick={() => onEditWorkOrder(w)} className="flex items-center justify-between px-3 py-2 rounded-lg bg-black/40 border border-white/10 cursor-pointer hover:border-white/25" data-testid={`profile-wo-${w.id}`}>
                <div><div className="text-sm text-white font-semibold">{w.title || w.service_type}</div><div className="text-xs text-white/40">{(w.status || "").replace("_", " ")} · {(w.parts || []).length} part(s)</div></div>
                <span className="text-xs text-[#F2B705]">${(w.actual_cost || w.estimated_cost || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Clock size={15} className="text-[#F2B705]" /> Maintenance Timeline</h3>
        {data.timeline.length === 0 ? <p className="text-white/40 text-sm">No maintenance history yet.</p> : (
          <div className="relative pl-4 border-l border-white/10 space-y-4" data-testid="profile-timeline">
            {data.timeline.map((t) => (
              <div key={t.id} className="relative">
                <Circle size={8} className="absolute -left-[21px] top-1 text-[#F2B705] fill-[#F2B705]" />
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <div className="text-white text-sm font-semibold">{t.mileage ? `${t.mileage.toLocaleString()} mi — ` : ""}{t.service}</div>
                  <div className="text-white/40 text-xs">{t.date || "—"}{t.cost ? ` · $${t.cost.toLocaleString()}` : ""}</div>
                </div>
                {t.vendor && <div className="text-white/30 text-xs">{t.vendor}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-black/40 border border-white/10 p-3">
      <div className="text-white/40 text-xs">{label}</div>
      <div className="text-white text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}
